import { logger } from '@adornis/base/logging.js';
import { SetBehaviorSubject } from '@adornis/base/rxjs-extension/index.js';
import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { getTypeString } from '@adornis/baseql/baseqlTypes.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { baseqlMetaData } from '@adornis/baseql/metadata/metaDataStore.js';
import { getAllFieldDefinitions } from '@adornis/baseql/metadata/resolve.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { matchingConfig } from '@adornis/bh-matching-config/config.js';
import { sendReminderMailForMatch } from '@adornis/bh-matching-mails/api/matching-mail.js';
import { sendFreeFormMailToBuddyUsers } from '@adornis/bh-matching-mails/api/user-mail.js';
import { BuddyMailTemplate, MailTemplateType } from '@adornis/bh-matching-mails/entities/mail-templates.js';
import { addCommentOnMatch, MatchAPI } from '@adornis/bh-matching-matches/api/matchAPI.js';
import { Match, MatchComment } from '@adornis/bh-matching-matches/entities/match.js';
import { HiddenSortingMachingUserFields } from '@adornis/bh-matching-matches/static/HiddenSortingMachingUserFields.js';
import { currentlySelectedTimeframe } from '@adornis/bh-matching-timeframes/components/matching-semester-selection.js';
import { BuddyUser, BuddyUserMentee, BuddyUserMentor } from '@adornis/bh-matching-user/entities/buddy-user.js';
import { getMenteeUserClass, getMentorUserClass } from '@adornis/bh-matching-user/user-class.js';
import { BHFilterEditor } from '@adornis/buchungsheld-components/bh-filter-editor.js';
import { TrDialog } from '@adornis/buchungsheld-theme/components/tr-dialog.js';
import '@adornis/buchungsheld-theme/components/tr-dropdown-selection.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { virtualize } from '@adornis/chemistry/directives/when-seen.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-card.js';
import '@adornis/chemistry/elements/components/x-empty-placeholder.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-grid.js';
import '@adornis/chemistry/elements/components/x-hr.js';
import '@adornis/chemistry/elements/components/x-loading-state.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import '@adornis/chemistry/elements/components/x-toggle-content.js';
import { buildFullOptions } from '@adornis/data-view/client/data-view-options.js';
import { View } from '@adornis/data-view/db/view.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { ListAspectSchema } from '@adornis/filter/aspect-schemas/ListAspect.js';
import { XInput } from '@adornis/forms/x-input.js';
import { SubscriptionPagination } from '@adornis/pagination/paginatable.js';
import '@adornis/popover/x-dropdown-selection.js';
import '@adornis/popover/x-tooltip.js';
import { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { format } from 'date-fns';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { repeat } from 'lit/directives/repeat.js';
import { combineLatest, of, Subject, switchMap, takeUntil, type Observable } from 'rxjs';
import './buddy-user-details.js';
import { MatchingDialogMatchesFreeFormMails } from './matching-dialog-matches-free-form-mails.js';

const searchFields = [
  'mentee.email',
  'mentee.firstName',
  'mentee.lastName',
  'mentor.email',
  'mentor.firstName',
  'mentor.lastName',
];

/**
 * @element buddy-page-manage-matches
 */
@customElement('buddy-page-manage-matches')
export class BuddyPageManageMatches extends ChemistryLitElement {
  protected _translator = new TranslationController<any>(this);

  @property({ attribute: false }) menteeFieldBlackList?: string[];
  @property({ attribute: false }) mentorFieldBlackList?: string[];
  @property({ attribute: false }) menteeUserClass: typeof BuddyUserMentee = BuddyUserMentee;
  @property({ attribute: false }) mentorUserClass: typeof BuddyUserMentor = BuddyUserMentor;

  protected readonly _filter = new AdornisFilter({
    searchFields,
  });

  pagination = new SubscriptionPagination(
    (skip: number, limit: number, filter: AdornisFilter, timeframeID: string) => {
      return !timeframeID
        ? () => of([])
        : (MatchAPI.subscribePaginatedAndFiltered(skip, limit, filter, timeframeID) as (
            gqlFields: any,
          ) => Observable<Match[]>);
    },
    [this._filter, ''],
    {
      ...selectionSet(() => Match, 2),
      mentor: selectionSet(() => BuddyUserMentor, 3, ['matches' as any]),
      mentee: selectionSet(() => BuddyUserMentee, 3, ['matches' as any]),
    },
  );

  filter = new Subject<{ filter: AdornisFilter; timeframeID: string }>();

  @state()
  matchesCount = new RXController(
    this,
    this.filter.pipe(
      switchMap(({ filter, timeframeID }) => {
        return MatchAPI.getMatchesCount(timeframeID, filter)();
      }),
    ),
  );

  @state()
  matchesMenteeCount = new RXController(
    this,
    this.filter.pipe(
      switchMap(({ filter, timeframeID }) => {
        return MatchAPI.getMenteesWithMatchesCount(timeframeID, filter)();
      }),
    ),
  );

  @state()
  matchesMentorCount = new RXController(
    this,
    this.filter.pipe(
      switchMap(({ filter, timeframeID }) => {
        return MatchAPI.getMentorsWithMatchesCount(timeframeID, filter)();
      }),
    ),
  );

  @state() _matches = new RXController(this, this.pagination.data);

  protected _sortingTarget = new RXController<'mentor' | 'mentee'>(this, 'mentor');

  protected readonly _isOpen = new SetBehaviorSubject(new Set<string>());
  // @ts-expect-error intentionally unused — side effect subscription for Lit reactivity
  @state() private readonly _isOpenRerender = new RXController(this, this._isOpen);
  protected readonly currentTimeframe = new RXController(this, currentlySelectedTimeframe);

  override connectedCallback(): void {
    super.connectedCallback();

    combineLatest([this._filter.whenChanging, currentlySelectedTimeframe])
      .pipe(takeUntil(this.disconnected))
      .subscribe(([filter, timeframe]) => {
        this.pagination.updateParameters(filter, timeframe?._id as any);
        this.filter.next({ filter, timeframeID: timeframe!._id });
      });
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.pagination.stop();
  }

  override render() {
    return html`
      <x-flex padding="md" space="md">
        <x-h1> ${this._translator.translate('route', 'matches')} (${this.matchesCount.value}) </x-h1>
        <x-hr></x-hr>
        <x-grid ${css({ gridTemplateColumns: 'min-content 1fr', gap: '10px' })}>
          <x-text bold style="white-space: nowrap">
            ${this._translator.translate('user', 'plural', getMenteeUserClass()._class)}:
          </x-text>
          <x-text> ${this.matchesMenteeCount.value} </x-text>
          <x-text bold style="white-space: nowrap">
            ${this._translator.translate('user', 'plural', getMentorUserClass()._class)}:
          </x-text>
          <x-text> ${this.matchesMentorCount.value} </x-text>
        </x-grid>
        <x-hr></x-hr>
        ${this._filterContent()} ${this._bulkMail()}
        ${
          this.pagination.loadingInitially.value
            ? html`<x-loading-state></x-loading-state>`
            : html`
                ${this._matchesList(this._matches.value, (index: number) => {
                  this.virtualizationOnRender(index, this.pagination);
                })}
              `
        }
      </x-flex>
    `;
  }

  // Match's `mentor`/`mentee` fields are typed as the abstract user classes,
  // so the field picker can only see abstract scalar fields when drilling.
  // For the concrete enum fields (gender, mainSubject, …) we expose them
  // explicitly via overridesForFieldName + ListAspectSchema, which gives a
  // dropdown of allowed values.
  private _enumFieldOverrides() {
    const overrides: Record<string, ReturnType<typeof ListAspectSchema>> = {};
    for (const [prefix, cls] of [
      ['mentee', this.menteeUserClass],
      ['mentor', this.mentorUserClass],
    ] as const) {
      const fields = getAllFieldDefinitions(cls);
      for (const field of fields) {
        let typeName: string;
        try {
          typeName = getTypeString(field.type);
        } catch {
          continue; // arrays, complex types — skip
        }
        const enumDef = baseqlMetaData.enums[typeName];
        if (!enumDef) continue;
        const values = Object.keys(enumDef.enum).filter(k => !k.startsWith('_'));
        const label = this._translator.translate('field', field.name as any);
        overrides[`${prefix}.${field.name}`] = ListAspectSchema(values, label);
      }
    }
    return overrides;
  }

  private _filterContent() {
    const fullOpt = buildFullOptions(
      { class: Match, view: new View({ filter: this._filter }) },
      {
        overridesForFieldName: this._enumFieldOverrides(),
        fieldAspectGeneration: {
          shouldIncludeField: bqlFieldDef =>
            ![
              '_id',
              '_class',
              'mentorID',
              'menteeID',
              'timeframeID',
              'username',
              'roles',
              'isOnline',
              'ios_sub',
              'emailConfirmed',
              'matches',
            ].includes(bqlFieldDef.name),
        },
      },
    );
    const targetEntityType = (this._filter.sortingFields[0] ?? '').includes('mentee')
      ? BuddyUserMentee
      : BuddyUserMentor;
    return html`
      ${BHFilterEditor.render({ options: fullOpt.filterOptions })}

      <x-flex horizontal space="xs" padding="xs" flex>
        <x-dropdown-selection
          flex
          placeholder="${this._translator.translate('field', 'sortingChooseField')}"
          @value-picked=${(choiceEvt: ValueEvent<'mentor' | 'mentee'>) => {
            const targetTypeKey = choiceEvt.detail.value;
            if (!targetTypeKey) return;

            this._sortingTarget.next(targetTypeKey);
            this._filter.sortingFields = ['_id'];
            this._filter.sortingDirections = ['asc'];
          }}
          .selectables=${['mentor', 'mentee']}
          .renderString=${(targetTypeKey: 'mentor' | 'mentee') => this._translator.translate(targetTypeKey, 'plural')}
        ></x-dropdown-selection>
        <x-dropdown-selection
          flex
          placeholder="${this._translator.translate('field', 'sortingChooseField')}"
          .value=${this._filter.sortingFields[0]?.split('.').at(-1) ?? '_id'}
          @value-picked=${(choiceEvt: ValueEvent<string>) => {
            const targetTypeKey = this._sortingTarget.value;
            const choice = choiceEvt.detail.value;
            if (!choice || !targetTypeKey) return;

            this._filter.sortingFields = [`${targetTypeKey}.${choice}`];
            this._filter.sortingDirections = ['asc'];
          }}
          .selectables=${Object.keys(targetEntityType.fields).filter(
            curAttr => !HiddenSortingMachingUserFields.includes(curAttr),
          )}
          .renderString=${(fieldAttr: string) => this._translator.translate('field', fieldAttr)}
        ></x-dropdown-selection>
        <x-dropdown-selection
          flex
          placeholder="${this._translator.translate('field', 'sortingChooseDirection')}"
          .value=${this._filter.sortingDirections[0] ?? 'asc'}
          @value-picked=${(choiceEvt: ValueEvent<boolean>) => {
            const choice = choiceEvt.detail.value;

            this._filter.sortingDirections = [choice ? 'asc' : 'desc'];
          }}
          .selectables=${[true, false]}
          .renderString=${(isDesc: boolean) =>
            this._translator.translate('field', isDesc ? 'sortingAsc' : 'sortingDesc')}
        ></x-dropdown-selection>
      </x-flex>
    `;
  }

  protected _bulkMail() {
    return html`
      <x-flex horizontal space-between>
        <x-button
          icon="mail"
          mode="outline"
          @click=${async () => {
            const timeFrame = this.currentTimeframe.value;
            if (!timeFrame) return;

            const matches = await MatchAPI.getFiltered(timeFrame._id, this._filter)(selectionSet(() => Match, 2));
            this._sendFreeFormMails(matches, BuddyUserMentor);
          }}
        >
          ${this._translator.translate('buddy', 'sendCircularMail', 'mentors')}
        </x-button>
        <x-button
          icon="mail"
          mode="outline"
          @click=${async () => {
            const timeFrame = this.currentTimeframe.value;
            if (!timeFrame) return;

            const matches = await MatchAPI.getFiltered(timeFrame._id, this._filter)(selectionSet(() => Match, 2));
            this._sendFreeFormMails(matches);
          }}
        >
          ${this._translator.translate('buddy', 'sendCircularMail', 'all')}
        </x-button>
        <x-button
          icon="mail"
          mode="outline"
          @click=${async () => {
            const timeFrame = this.currentTimeframe.value;
            if (!timeFrame) return;

            const matches = await MatchAPI.getFiltered(timeFrame._id, this._filter)(selectionSet(() => Match, 2));
            this._sendFreeFormMails(matches, BuddyUserMentee);
          }}
        >
          ${this._translator.translate('buddy', 'sendCircularMail', 'mentees')}
        </x-button>
      </x-flex>
    `;
  }

  protected _sendFreeFormMails(targetMatch: Match[], targetClass?: typeof BuddyUserMentor | typeof BuddyUserMentee) {
    const mailAddresses = MatchAPI.getAllEmailAddressFromMatches(targetMatch, targetClass);

    MatchingDialogMatchesFreeFormMails.showPopup({
      props: {
        allMatches: targetMatch,
        mailAddresses,
        mailTemplateType: MailTemplateType.FREE_FORM,
        copyMailAddress: matchingConfig.get('DIRECT_MAIL_COPY_ADDRESS'),
        mailTemplateConstructor: (doc: PartialEntityData<BuddyMailTemplate>) => new BuddyMailTemplate(doc),
        mailSendOperation: (matchIDs: string[], mailTemplate: BuddyMailTemplate) => {
          return sendFreeFormMailToBuddyUsers(matchIDs, mailTemplate, targetClass?._class);
        },
      },
    });
  }

  private _matchesList(matches: Maybe<Match[]>, onReachEnd: (index: number) => void) {
    if (!matches?.length)
      return html`
        <x-empty-placeholder
          .text=${this._translator.translate('buddy', 'noMatchesYet')}
          .img=${'close'}
        ></x-empty-placeholder>
      `;

    return html`
      <x-flex space="sm"> ${repeat(matches, (match, index) => this._matchItem(match, index, onReachEnd))} </x-flex>
    `;
  }

  protected _matchItem(
    match: Match,
    index: number,
    onLoad: (index: number) => void,
  ): import('lit/directive.js').DirectiveResult {
    const mentor = match.mentor as Maybe<BuddyUserMentor>;
    const mentee = match.mentee as Maybe<BuddyUserMentee>;

    const isOpen = this._isOpen.has(match._id);

    const footer = () => html`
      <x-flex horizontal end>
        <x-button
          mode="text"
          icon="delete"
          tone="error"
          @click=${() =>
            TrDialog.guard(() => match.remove(), {
              hasToConfirm: true,
              confirmText: this._translator.translate('buddy', 'removeMatchConfirm'),
              success: () => XSnackbar.show(this._translator.translate('buddy', 'removeMatchSuccessful')),
            })}
        >
          ${this._translator.translate('buddy', 'removeMatch')}
        </x-button>
      </x-flex>
      <x-hr></x-hr>
      ${this._userComments(match)}
    `;

    const userDetail = user => {
      if (!user)
        return html`
          <x-flex center flex>
            <x-text tone="warning"> - User wurde gelöscht - </x-text>
          </x-flex>
        `;
      const entity = user instanceof BuddyUserMentor ? BuddyUserMentor : BuddyUserMentee;
      const blacklist = user instanceof BuddyUserMentor ? this.mentorFieldBlackList : this.menteeFieldBlackList;

      return html`
        <buddy-user-details
          flex
          hide-toggle
          ?always-open=${isOpen}
          .user=${user}
          .overrideActions=${{
            sendReminderMailToUser: () => html`
              <tr-button-small
                mode="icon"
                icon="mail"
                tone=${this.colors.tone.onyx}
                @click=${() => this._sendReminderMail(match, user)}
              >
                <x-tooltip>${this._translator.translate('buddy', 'matchActions', 'sendReminderMail')}</x-tooltip>
              </tr-button-small>
            `,
            sendMailToUser: () => html`
              <tr-button-small
                mode="icon"
                icon="mail"
                tone=${this.colors.tone.onyx}
                @click=${() => this._sendFreeFormMails([match], entity)}
              >
                <x-tooltip>${this._translator.translate('buddy', 'matchActions', 'sendFreeformMail')}</x-tooltip>
              </tr-button-small>
            `,
          }}
          .fieldBlackList=${blacklist}
        ></buddy-user-details>
      `;
    };

    return virtualize(
      html`
        <x-card ${css({ width: '100%', position: 'relative' })} @click=${() => this._isOpen.add(match._id)} space="md">
          <x-flex space="md">
            <x-flex horizontal space="md" ${css({ paddingTop: this.spacing.md })}>
              ${[
                userDetail(mentor), //
                userDetail(mentee),
              ]}
            </x-flex>

            ${isOpen ? footer() : nothing}
          </x-flex>

          <!--  -->
          ${
            !!mentor?.note?.replace(/(<([^>]+)>)/gi, '')
              ? html`
                  <x-icon
                    ${css({
                      fontSize: '18px',
                      position: 'absolute',
                      top: this.spacing.sm,
                      left: this.spacing.md,
                      color: this.colors.tone.warning,
                      opacity: '.6',
                    })}
                    tone
                  >
                    note
                    <x-tooltip
                      .text=${html` <x-text> ${this._translator.translate('user', 'note_exists')} </x-text> `}
                    ></x-tooltip>
                  </x-icon>
                `
              : nothing
          }
          ${
            !!mentee?.note?.replace(/(<([^>]+)>)/gi, '')
              ? html`
                  <x-icon
                    ${css({
                      fontSize: '18px',
                      position: 'absolute',
                      top: this.spacing.sm,
                      left: `calc(${this.spacing.sm} + 50%)`,
                      color: this.colors.tone.warning,
                      opacity: '.6',
                    })}
                    tone
                  >
                    note
                    <x-tooltip
                      .text=${html` <x-text> ${this._translator.translate('user', 'note_exists')} </x-text> `}
                    ></x-tooltip>
                  </x-icon>
                `
              : nothing
          }

          <x-button
            mode=${isOpen ? 'icon' : 'text'}
            icon=${isOpen ? 'expand_more' : 'chevron_right'}
            ${css({ position: 'absolute', top: this.spacing.sm, right: this.spacing.sm })}
            @click=${(e: MouseEvent) => {
              e.stopPropagation();
              if (isOpen) this._isOpen.delete(match._id);
              else this._isOpen.add(match._id);
            }}
          >
            ${isOpen ? '' : this._translator.translate('buddy', 'showAll')}
          </x-button>
        </x-card>
      `,
      oldHeight => html` <div ${css({ height: `${oldHeight}px`, gridColumn: '1 / -1' })}></div> `,
      {
        wrapperDisplayContents: true,
        onRender: () => {
          onLoad(index);
        },
      },
    );
  }

  protected async _sendReminderMail(match: Match, user?: BuddyUser) {
    if (!user) return TrDialog.alert('Kann keine Erinnerungsmail an nicht vorhandenen Nutzer schicken.');

    try {
      await TrDialog.waitFor(sendReminderMailForMatch(match._id, user._id)());
      void XSnackbar.show('Erinnerungsmail wurde erfolgreich versendet.');
    } catch (err) {
      logger.error({ err });
      void TrDialog.alert('Konnte keine Erinnerungsmail verschicken. Bitte versuchen Sie es später erneut.');
    }
  }

  private _newCommentElement = createRef<XInput>();
  protected _userComments(match: Match) {
    let newComment = '';
    return html`
      <x-flex space="xs">
        <x-text bold>
          ${this._translator.translate('buddy', 'history')} / ${this._translator.translate('buddy', 'comments')}
        </x-text>
        ${repeat(
          match.comments ?? [],
          comment => comment._id,
          comment => html`
            <x-flex
              horizontal
              crossaxis-center
              space-between
              space="md"
              ${css({
                ...(comment.isAlert ? { background: `${this.colors.tone.warning}` } : {}),
                borderRadius: this.sizes.borderRadiusSecondary,
              })}
              padding=${comment.isAlert ? 'xs' : ''}
            >
              <x-text flex> ${comment.text} </x-text>
              <x-text tone="subtle">
                ${comment.createdAt ? format(comment.createdAt, 'dd.LL.yyyy HH:MM') : '-'}
              </x-text>
              <x-flex horizontal crossaxis-center space="xxs">
                <x-button mode="icon" icon="warning" @click=${() => this._markCommentAsAlert(comment, match)}>
                </x-button>
                <x-button mode="icon" icon="delete" tone="error" @click=${() => this._removeComment(comment, match)}>
                </x-button>
              </x-flex>
            </x-flex>
          `,
        )}
        <x-flex horizontal crossaxis-center space="sm">
          <x-input
            placeholder-mode="hide"
            flex
            placeholder="Kommentar"
            ${ref(this._newCommentElement)}
            @value-changed=${(e: ValueEvent<string>) => (newComment = e.detail.value)}
            @submit=${(e: KeyboardEvent) => this._addComment(newComment, match)}
          ></x-input>
          <x-button mode="icon" icon="send" @click=${() => this._addComment(newComment, match)}></x-button>
        </x-flex>
      </x-flex>
    `;
  }

  private async _addComment(comment: string, match: Match) {
    if (!comment || !match) return;

    try {
      this._newCommentElement.value?.value.next('');
      await TrDialog.waitFor(addCommentOnMatch(match._id, comment)());
      void XSnackbar.show('Kommentar erfolgreich hinzugefügt.');
    } catch (err) {
      logger.error({ err });
      void TrDialog.alert('Es gab ein Problem beim Hinzufügen eines Kommentars. Bitte versuchen Sie es später erneut.');
    }
  }

  private async _markCommentAsAlert(comment: MatchComment, match: Match) {
    if (!match) return;
    comment.isAlert = !comment.isAlert;

    try {
      await TrDialog.waitFor(match.save());
      this.requestUpdate();
    } catch (err) {
      logger.error({ err });
      void TrDialog.alert(
        'Es gab ein Problem beim Wechseln des Kommentar-Status. Bitte versuchen Sie es später erneut.',
      );
    }
  }

  private async _removeComment(comment: MatchComment, match: Match) {
    if (!comment || !match?.comments) return;

    if (!(await TrDialog.confirm('Soll dieser Kommentar wirklich gelöscht werden?'))) return;

    try {
      const commentIndex = match.comments.findIndex(c => c._id === comment._id);
      if (commentIndex === -1)
        return TrDialog.alert('Es gab ein Problem beim Löschen des Kommentars. Bitte versuchen Sie es später erneut.');
      match.comments.splice(commentIndex, 1);
      await TrDialog.waitFor(match.save());
      void XSnackbar.show('Kommentar wurde erfolgreich gelöscht.');
    } catch (err) {
      logger.error({ err });
      void TrDialog.alert('Es gab ein Problem beim Löschen des Kommentars. Bitte versuchen Sie es später erneut.');
    }
  }

  protected virtualizationOnRender(index: number, pagination: SubscriptionPagination<any, any>) {
    if (
      (index === pagination.data.length - 1 || index === pagination.data.length - 1) &&
      pagination.totalRequested.value < index + 80
    ) {
      pagination.next();
    }
  }
}
